import type { CcActivityItem } from "@/api/code-companion";

/**
 * Traducao do feed de atividade de linguagem de banco pra linguagem de gente.
 *
 * A tela mostrava `context_approved #4`: o nome da coluna e a chave primaria. Quem
 * le a tela nao sabe o que e o pacote 4 nem precisa aprender o vocabulario da
 * tabela pra acompanhar o proprio projeto.
 *
 * O descritor sai daqui sem texto nenhum de proposito: quem monta a frase e o
 * componente, com a chave escrita a mao, pra que o guarda de i18n a enxergue. Um
 * mapa de templates aqui dentro passaria despercebido pelo guarda e voltaria em
 * ingles no meio de uma tela em portugues.
 */

export type ActivityKind =
  | "assigned"
  | "blocker_raised"
  | "context_approved"
  | "flag_raised"
  | "flag_resolved"
  | "object_status"
  | "progress"
  | "ready_for_deploy"
  | "status_change"
  | "unknown";

export interface ActivityDescriptor {
  kind: ActivityKind;
  /** Nome do pacote quando conhecido; o `#id` so sobra quando nao ha nome. */
  target: string | null;
  /** Complemento que ja vem do payload (status novo, tipo de flag, objeto). */
  detail: string | null;
  /** So no `unknown`: o type cru, pra tela nao engolir evento novo em silencio. */
  raw: string;
}

const CONHECIDOS: ReadonlySet<string> = new Set([
  "assigned",
  "blocker_raised",
  "context_approved",
  "flag_raised",
  "flag_resolved",
  "object_status",
  "progress",
  "ready_for_deploy",
  "status_change",
]);

function pickText(payload: Record<string, unknown> | null, ...fields: string[]): string | null {
  for (const c of fields) {
    const v = payload?.[c];
    if (typeof v === "string" && v.trim()) return v;
    if (typeof v === "number") return String(v);
  }
  return null;
}

export function activityDescriptor(
  item: CcActivityItem,
  packageLabel: (id: number) => string | null,
): ActivityDescriptor {
  const packageName = item.package_id != null ? packageLabel(item.package_id) : null;
  return {
    kind: (CONHECIDOS.has(item.type) ? item.type : "unknown") as ActivityKind,
    target: packageName ?? (item.package_id != null ? `#${item.package_id}` : null),
    detail: pickText(
      item.payload,
      "object_name",
      "flag_kind",
      "object_status",
      "status",
      "to",
      "progress_pct",
    ),
    raw: item.type,
  };
}

/**
 * Tempo relativo com a granularidade que a data merece.
 *
 * `toLocaleString()` gastava a linha inteira com uma data completa quando a
 * pergunta e "isso foi agora ou semana passada". A data exata continua acessivel
 * no `title` do elemento.
 */
export function formatRelativeTime(iso: string, now: Date, locale: string): string {
  const ms = new Date(iso).getTime() - now.getTime();
  if (!Number.isFinite(ms)) return iso;
  const seg = Math.round(ms / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const escalas: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, "second"],
    [3600, "minute"],
    [86400, "hour"],
    [604800, "day"],
    [2629800, "week"],
    [31557600, "month"],
  ];
  let anterior = 1;
  for (const [limite, unidade] of escalas) {
    if (Math.abs(seg) < limite) return rtf.format(Math.round(seg / anterior), unidade);
    anterior = limite;
  }
  return rtf.format(Math.round(seg / 31557600), "year");
}
